import { Form } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { creatorCollabTransitionsPath } from "@/routes"

/**
 * OKS-988 — "Request another anyway" (UX Spec §S3).
 *
 * The TTL guess is only a guess. Lipstick runs out, food gets eaten. When the
 * creator says they need another, they say why in their own words and the
 * request is granted like any other — the brand decides by shipping or
 * cancelling, with the sentence in front of them. Participation is never
 * blocked on that decision.
 *
 * Inertia `<Form>` + plain `Label` / `Textarea` (Crew rule). No
 * react-hook-form, no zod.
 *
 * # ⚠️ The reason is checked in TWO places, and that is deliberate
 *
 * The server is the gate: `Collabs::RequestProduct` refuses an empty or
 * over-length reason, so a forged POST cannot get past it. The check here is
 * not a duplicate of that authority — it is what keeps the ordinary case from
 * making a round trip it doesn't need, which is also what keeps the typed text
 * on screen for free.
 *
 * When the server does refuse (a forged post, or a race), the message comes
 * back as an Inertia field error on `rerequest_reason` and the typed text
 * comes back in `flash.rerequest_reason_draft`. Both are needed: the error
 * alone would reopen the dialog empty, which reads to the creator as "my text
 * was thrown away".
 *
 * # ⚠️ Send stays ENABLED on an empty textarea (UX-A3)
 *
 * A disabled Send would leave the creator with a control that silently does
 * nothing and no explanation of why. Tapping it states the rule instead.
 *
 * # ⚠️ Over-length is an ERROR, never a hard `maxLength` cap (UX-A4)
 *
 * A silent trim would ship a truncated sentence to the brand as if the creator
 * had written it that way. There is also no native `required` here — Crew
 * validates server-side and renders localized inline errors; a bare `required`
 * fires Chrome's untranslated bubble on the FR-default product (OKS-661/692).
 */
export const REQUEST_ANOTHER_MAX_CHARS = 200

export function RequestAnotherDialog({
  open,
  onOpenChange,
  collabId,
  brandName,
  serverError,
  draftReason,
  offerCount = 0,
  flaggedProduct,
  onHandOff,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  collabId: number
  brandName: string
  /** Inertia field error on `rerequest_reason`, when the server refused. */
  serverError?: string
  /** The creator's text, echoed back through the flash after a refusal. */
  draftReason?: string
  /**
   * OKS-990 decision 4 — how many products the mission offers, and the name of
   * the one the guard flagged. At N >= 2 the body is REPLACED by the variant
   * naming the consequence: the reason is required even for a product the
   * creator has never received.
   */
  offerCount?: number
  flaggedProduct?: string
  /**
   * OKS-990 F-MOUNT, route 2 — hand the reason to the picker instead of
   * posting it.
   *
   * ⚠️ WITHOUT THIS THE RE-REQUEST PATH IS A DEAD END ON A MULTI-PRODUCT DROP.
   * This dialog posts `event=apply` + `no_ship=false` + the reason and NO
   * product. On a mission where `CreatorChoiceSet.picker_applies?`, the server
   * answers `:product_required` (`PickResolver`) — correctly — and the field
   * error lands on `mission_product_id`, which this dialog does not render.
   * The creator sees a submit that does nothing and is told nothing.
   *
   * UX §36 orders it explicitly: reason dialog → picker → grant. The reason
   * travels WITH the pick so the whole re-request is one POST and one
   * transaction, rather than a refusal round-trip used as control flow.
   */
  onHandOff?: (reason: string) => void
}) {
  const { t } = useTranslation()
  const [reason, setReason] = useState(draftReason ?? "")
  const [clientError, setClientError] = useState<string | null>(null)

  // Re-seed when the server hands a NEW draft back, so a refused submit
  // reopens with the creator's words rather than an empty box.
  //
  // ⚠️ Adjusted during render, not in an effect. React documents this as the
  // way to reset state when a prop changes; doing it in `useEffect` renders
  // the stale value first and then triggers a cascading second render, which
  // is what `react-hooks` flags. Tracking the previous prop is what keeps this
  // from clobbering what the creator is typing right now — a bare
  // `if (draftReason !== reason)` would fight the textarea on every keystroke.
  const [lastDraft, setLastDraft] = useState(draftReason)
  if (draftReason !== lastDraft) {
    setLastDraft(draftReason)
    setReason(draftReason ?? "")
  }

  const trimmed = reason.trim()
  const overLimit = reason.length > REQUEST_ANOTHER_MAX_CHARS
  const error = clientError ?? serverError ?? null
  const errorId = "request-another-error"

  function validate(): boolean {
    if (trimmed.length === 0) {
      setClientError(t("collabs.transitions.errors.reason_required"))
      return false
    }
    if (overLimit) {
      setClientError(t("collabs.transitions.errors.reason_too_long"))
      return false
    }
    setClientError(null)
    return true
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="request-another-dialog">
        <DialogHeader>
          <DialogTitle>{t("collabs.request_another.title")}</DialogTitle>
          <DialogDescription>
            {/* ⚠️ BOTH conditions, and the second is not defensive padding.
                The multi body interpolates the held product by name; with
                `product: flaggedProduct ?? ""` a missing name renders "tu as
                déjà ." — a sentence about nothing, shown at the exact moment
                the creator is being asked to justify a re-request. Falling
                back to the shipped single-product body says less but says it
                truthfully. `entry.recency` is null on the A12 path (brand
                pre-set no-ship, guard never flagged), so this is reachable. */}
            {offerCount >= 2 && flaggedProduct
              ? t("collabs.request_another.body_multi", {
                  brand: brandName,
                  product: flaggedProduct,
                })
              : t("collabs.request_another.body", { brand: brandName })}
          </DialogDescription>
        </DialogHeader>

        <Form
          action={creatorCollabTransitionsPath(collabId)}
          method="post"
          onBefore={validate}
        >
          {({ processing }) => (
            <>
              <input type="hidden" name="event" value="apply" />
              {/* The creator is asking for a shipment despite already holding
                  one — the opposite of the no-ship path, stated explicitly so
                  the server never has to infer it from the reason's presence. */}
              <input type="hidden" name="no_ship" value="false" />

              <div className="min-w-0 space-y-2">
                <Label htmlFor="rerequest_reason">
                  {t("collabs.request_another.field_label")}
                </Label>
                {/* ⚠️ OKS-988 F3 — `wrap-anywhere` + `min-w-0`.
                    Measured: a 210-character unbroken token (a pasted URL or a
                    hashtag chain) grew the textarea to 1614px and pushed Send
                    and Cancel outside the dialog, so the creator could neither
                    submit nor escape. `break-words` alone does not break a
                    single token, and without `min-w-0` the flex column refuses
                    to shrink below its content. Same treatment UX-A7 mandates
                    on the brand side, for the same input. */}
                <Textarea
                  id="rerequest_reason"
                  name="rerequest_reason"
                  className="min-w-0 [overflow-wrap:anywhere]"
                  rows={4}
                  autoFocus
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value)
                    if (clientError) setClientError(null)
                  }}
                  readOnly={processing}
                  placeholder={t("collabs.request_another.placeholder")}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? errorId : undefined}
                  data-testid="request-another-reason"
                />
                <div className="flex items-center justify-between">
                  <p
                    className={cn(
                      "text-xs",
                      overLimit ? "text-destructive" : "text-muted-foreground",
                    )}
                    data-testid="request-another-counter"
                  >
                    {t("collabs.request_another.counter", {
                      count: reason.length,
                    })}
                  </p>
                </div>
                {error && (
                  <p
                    id={errorId}
                    role="alert"
                    aria-live="polite"
                    className="text-destructive text-sm"
                    data-testid="request-another-error"
                  >
                    {error}
                  </p>
                )}
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  {t("collabs.request_another.cancel")}
                </Button>
                {/* Enabled even when empty — see UX-A3 in the file header.

                    ⚠️ TWO BUTTONS, not one button with a branch inside
                    `onClick`. A `type="submit"` that conditionally calls
                    `preventDefault` still submits whenever the branch is
                    misjudged, and Enter in the textarea submits it regardless
                    of what the click handler does. Rendering `type="button"`
                    on the hand-off path means the productless POST is not
                    reachable from this dialog at all, rather than merely
                    avoided. */}
                {onHandOff ? (
                  <Button
                    type="button"
                    data-testid="request-another-handoff"
                    onClick={() => {
                      if (validate()) onHandOff(reason.trim())
                    }}
                  >
                    {t("collabs.request_another.submit")}
                  </Button>
                ) : (
                  <Button type="submit" disabled={processing}>
                    {processing
                      ? t("collabs.request_another.working")
                      : t("collabs.request_another.submit")}
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
